import { useState, useMemo, useCallback, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { Button } from '@/components/ui/Button/Button';
import { api } from '@/api/client';
import type { MessengerThread } from '@/api/client';
import type { ChatContactUser } from '@/components/Messenger/sidebar/ChatSidebar';
import { getUserDisplayName } from '@/components/Messenger/sidebar/ChatSidebar';
import { matchesChatContactSearchQuery } from '@/components/Messenger/shared/chatContactSearch';
import { MessengerContactPickerPanel } from '@/components/Messenger/shared/MessengerContactPickerPanel';
import { MessengerPickerContactRow } from '@/components/Messenger/shared/MessengerPickerContactRow';

type CreateGroupChatModalProps = {
  open: boolean;
  users: ChatContactUser[];
  currentUserId: string | null;
  onClose: () => void;
  onThreadsRefresh?: () => void | Promise<void>;
} & (
  | { variant?: 'create'; onCreated: (thread: MessengerThread) => void }
  | { variant: 'addMembers'; threadId: string; onMembersAdded: () => void | Promise<void> }
);

export function CreateGroupChatModal(props: CreateGroupChatModalProps) {
  const { open, users, currentUserId, onClose, onThreadsRefresh } = props;
  const isAddMembers = props.variant === 'addMembers';
  const threadId = isAddMembers ? props.threadId : undefined;
  const onCreatedFn = !isAddMembers ? props.onCreated : undefined;
  const onMembersAddedFn = isAddMembers ? props.onMembersAdded : undefined;

  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [existingMemberIds, setExistingMemberIds] = useState<Set<string>>(() => new Set());
  const [membersLoadState, setMembersLoadState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setSelected(new Set());
    setQuery('');
    setSubmitting(false);
    if (isAddMembers && threadId) {
      setMembersLoadState('loading');
      void api.messenger
        .getThreadMembers(threadId)
        .then((r) => {
          setExistingMemberIds(new Set(r.members.map((m) => m.person_id)));
          setMembersLoadState('done');
        })
        .catch(() => {
          setExistingMemberIds(new Set());
          setMembersLoadState('error');
        });
    } else {
      setMembersLoadState('idle');
      setExistingMemberIds(new Set());
    }
  }, [open, isAddMembers, threadId]);

  const q = query.trim().toLowerCase();

  const filteredUsers = useMemo(
    () => (q.length >= 1 ? users.filter((u) => matchesChatContactSearchQuery(u, q)) : users),
    [users, q]
  );

  const pickerUsersSorted = useMemo(() => {
    if (isAddMembers && membersLoadState === 'loading') return [];
    if (isAddMembers && membersLoadState === 'error') return [];
    const exclude = isAddMembers ? existingMemberIds : new Set<string>();
    const visible = filteredUsers.filter((u) => u.id !== currentUserId && !exclude.has(u.id));
    return [...visible].sort((a, b) =>
      getUserDisplayName(a).localeCompare(getUserDisplayName(b), 'ru')
    );
  }, [isAddMembers, membersLoadState, existingMemberIds, filteredUsers, currentUserId]);

  const toggleUser = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [onClose, submitting]);

  const handleSubmit = useCallback(async () => {
    if (selected.size < 1 || submitting) return;
    if (!isAddMembers) {
      const t = title.trim();
      if (!t) return;
    } else {
      if (!threadId || membersLoadState !== 'done' || !onMembersAddedFn) return;
    }

    setSubmitting(true);
    try {
      if (isAddMembers && threadId && onMembersAddedFn) {
        for (const id of selected) {
          await api.messenger.addThreadMember(threadId, id);
        }
        await onThreadsRefresh?.();
        await onMembersAddedFn();
        onClose();
      } else if (onCreatedFn) {
        const t = title.trim();
        const thread = await api.messenger.createGroupThread({
          title: t,
          member_ids: [...selected],
        });
        await onThreadsRefresh?.();
        onCreatedFn(thread);
        onClose();
      }
    } catch {
      setSubmitting(false);
    }
  }, [
    selected,
    submitting,
    isAddMembers,
    threadId,
    membersLoadState,
    title,
    onThreadsRefresh,
    onClose,
    onCreatedFn,
    onMembersAddedFn,
  ]);

  if (!open) return null;

  const modalTitle = isAddMembers ? 'Добавить участника' : 'Новая группа';
  const listLoading = isAddMembers && membersLoadState === 'loading';
  const listError = isAddMembers && membersLoadState === 'error';
  const listReady = !isAddMembers || membersLoadState === 'done';

  const canSubmit =
    selected.size >= 1 &&
    !submitting &&
    listReady &&
    (isAddMembers ? true : title.trim().length > 0);

  const pickerLayout = isAddMembers ? 'addMembers' : 'create';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      size="md"
      headerVariant="island"
      containedOverlay
      className="messengerContactPicker"
    >
      <div
        className="messengerContactPicker__inner"
        data-picker-layout={pickerLayout}
      >
        {!isAddMembers ? (
          <div className="messengerContactPicker__titleField">
            <Input
              id="createGroupTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Название группы"
              maxLength={200}
              disabled={submitting}
              fullWidth
              inputClassName="messengerContactPicker__titleInput"
              aria-label="Название группы"
            />
          </div>
        ) : null}

        <MessengerContactPickerPanel
          sectionLabel="Добавить участников"
          query={query}
          onQueryChange={setQuery}
          searchDisabled={submitting || listLoading}
          searchId="messengerPicker-createGroup-search"
          footer={
            <div className="messengerContactPicker__actions">
              <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                Отмена
              </Button>
              <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
                {submitting
                  ? isAddMembers
                    ? 'Добавление…'
                    : 'Создание…'
                  : isAddMembers
                    ? 'Добавить'
                    : 'Создать'}
              </Button>
            </div>
          }
        >
          {listLoading ? (
            <div className="messengerContactPicker__listStatus">Загрузка…</div>
          ) : null}
          {listError ? (
            <div className="messengerContactPicker__listStatus messengerContactPicker__listStatus_error">
              Не удалось загрузить список участников группы
            </div>
          ) : null}
          {listReady && !listError && pickerUsersSorted.length === 0 ? (
            <div className="messengerContactPicker__listStatus">Нет доступных контактов</div>
          ) : null}
          {listReady && !listError
            ? pickerUsersSorted.map((u) => (
                <MessengerPickerContactRow
                  key={u.id}
                  mode="multi"
                  user={u}
                  checked={selected.has(u.id)}
                  disabled={submitting}
                  onToggle={toggleUser}
                />
              ))
            : null}
        </MessengerContactPickerPanel>
      </div>
    </Modal>
  );
}
